// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {ShareThemeModal} from '@app/features/theme/components/modals/ShareThemeModal';
import Theme from '@app/features/theme/state/Theme';
import ThemeLibrary from '@app/features/theme/state/ThemeLibrary';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ColorPickerField} from '@app/features/ui/components/form/ColorPickerField';
import {Input} from '@app/features/ui/components/form/Input';
import {Spinner} from '@app/features/ui/components/Spinner';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/ThemeTab.module.css';
import {useLingui} from '@lingui/react/macro';
import {ShareNetworkIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo} from 'react';
import {ThemeLibraryManager} from './ThemeLibraryManager';
import {THEME_COLOR_VARIABLES, THEME_FONT_VARIABLES} from './themeConstants';
import {cssColorStringToNumber, extractThemeVariableOverrides, numberToHex, updateCssForVariable} from './themeUtils';
import {useThemeHydration} from './useThemeHydration';
export const CustomizeThemeModal: React.FC = observer(() => {
	const {t} = useLingui();
	const themePreference = Theme.themePreference;
	const customThemeCss = Accessibility.customThemeCss ?? '';
	const {defaultVariableValues} = useThemeHydration(themePreference, customThemeCss);
	const overrides = useMemo(() => extractThemeVariableOverrides(customThemeCss), [customThemeCss]);
	const handleShareTheme = useCallback(() => {
		const css = [ThemeLibrary.activeThemeCss, Accessibility.customThemeCss ?? '']
			.map((value) => value.trim())
			.filter(Boolean)
			.join('\n\n');
		if (!css.trim()) {
			ToastCommands.error(t`You don't have any custom theme overrides to share yet.`);
			return;
		}
		ModalCommands.push(modal(() => <ShareThemeModal themeCss={css} />));
	}, [t]);
	const handleResetAllOverrides = useCallback(() => {
		AccessibilityCommands.update({customThemeCss: null});
	}, []);
	return (
		<Modal.Root size="large">
			<Modal.Header title={t`Customize Theme`} />
			<Modal.Content>
				{defaultVariableValues === null ? (
					<div className={styles.loadingContainer}>
						<Spinner size="large" />
					</div>
				) : (
					<div className={styles.customizeBody}>
						<ThemeLibraryManager />
						<div className={styles.tokenSection}>
							<div className={styles.colorGrid}>
								{THEME_FONT_VARIABLES.map((variableName) => {
									const overrideValue = overrides[variableName];
									const defaultValue = defaultVariableValues[variableName];
									const currentValue = overrideValue ?? defaultValue ?? '';
									return (
										<Input
											key={variableName}
											label={
												variableName === '--font-sans'
													? t`Body font family`
													: variableName === '--font-mono'
														? t`Monospace font family`
														: variableName
											}
											placeholder={defaultValue || undefined}
											value={currentValue}
											onChange={(event) => {
												const next = event.target.value.trim();
												const updatedCss =
													next.length === 0
														? updateCssForVariable(customThemeCss, variableName, null)
														: updateCssForVariable(customThemeCss, variableName, next);
												AccessibilityCommands.update({customThemeCss: updatedCss});
											}}
										/>
									);
								})}
							</div>
							<div className={styles.colorSection}>
								<div className={styles.colorSectionHeading}>{t`Colors`}</div>
								<div className={styles.colorGrid}>
									{THEME_COLOR_VARIABLES.map((variableName) => {
										const overrideCss = overrides[variableName];
										const defaultCss = defaultVariableValues[variableName];
										const defaultNumber =
											defaultCss !== undefined ? (cssColorStringToNumber(defaultCss) ?? 0) : undefined;
										const valueNumber =
											overrideCss !== undefined ? (cssColorStringToNumber(overrideCss) ?? defaultNumber ?? 0) : 0;
										const label = variableName.replace(/^--/, '').replace(/-/g, ' ');
										return (
											<ColorPickerField
												key={variableName}
												label={label}
												value={valueNumber ?? 0}
												defaultValue={defaultNumber}
												hideHelperText
												onChange={(nextValue) => {
													const updatedCss =
														nextValue === 0
															? updateCssForVariable(customThemeCss, variableName, null)
															: updateCssForVariable(customThemeCss, variableName, numberToHex(nextValue));
													AccessibilityCommands.update({customThemeCss: updatedCss});
												}}
											/>
										);
									})}
								</div>
							</div>
							<div className={styles.buttonGroup}>
								<Button variant="secondary" fitContent onClick={handleResetAllOverrides}>
									{t`Reset token overrides to theme default`}
								</Button>
								<Button
									variant="primary"
									fitContent
									leftIcon={<ShareNetworkIcon size={18} />}
									onClick={handleShareTheme}
								>
									{t`Share active custom theme`}
								</Button>
							</div>
						</div>
					</div>
				)}
			</Modal.Content>
		</Modal.Root>
	);
});
